import { Button, Card, FormattedMessageDiv, ToggleCardGroup } from '@odmonorepo/core-components';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';

export interface BusinessOption<TDetails = unknown> {
  id: string;
  legalName: string;
  businessAddress?: {
    address?: string;
    city?: string;
    region?: string;
    postalCode?: string;
  };
  inceptionDate?: string;
  /**
   * The full, untrimmed business record this option was derived from.
   * Passed back through `onSelect` so callers can persist the complete
   * business details (e.g. write to Komodo on submit).
   */
  details: TDetails;
}

export interface BusinessSelectProps<TDetails = unknown> {
  businesses: BusinessOption<TDetails>[];
  onSelect: (business: BusinessOption<TDetails>) => void;
  onNoneOfTheAbove?: () => void;
  onBack?: () => void;
}

function formatAddressCityLine(address: BusinessOption['businessAddress']): string | null {
  if (!address) return null;
  const cityState = [address.city, address.region].filter(Boolean).join(', ');
  const parts = [cityState, address.postalCode].filter(Boolean);
  return parts.join('  ') || null;
}

function formatDate(isoDate?: string): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

export const BusinessSelect = <TDetails,>({ businesses, onSelect, onNoneOfTheAbove: onNoneSelected, onBack }: BusinessSelectProps<TDetails>) => {
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessOption<TDetails> | undefined>(businesses[0]);
  const [noneSelected, setNoneSelected] = useState(false);

  const handleBusinessChange = (business: BusinessOption<TDetails>) => {
    setSelectedBusiness(business);
    setNoneSelected(false);
  };

  return (
    <Stack gap={5} className="w-100 mx-auto px-2 px-md-0" style={{ maxWidth: '600px' }}>
      <Card className="border-0 shadow-sm fs-6" bodyClassName="p-5">
        <Stack gap={5}>
          <div>
            <FormattedMessageDiv className="h4 mb-2" id="businessSelect.title" />
            <FormattedMessageDiv className="mb-0 fw-normal" id="businessSelect.description" />
          </div>
          <ToggleCardGroup
            flexDirection="column"
            className="gap-5"
            showRadioBtn
            name="business-selection"
            radioContainerClassName="col-auto align-self-start pt-1 ms-0"
            radioInputClassName="me-3"
            value={businesses.slice(0, 3)}
            selectedIdx={selectedBusiness ? businesses.slice(0, 3).findIndex(b => b.id === selectedBusiness.id) : undefined}
            onChange={handleBusinessChange}
          >
            {businesses.slice(0, 3).map((business) => {
              const cityLine = formatAddressCityLine(business.businessAddress);
              const registrationDate = formatDate(business.inceptionDate);
              return (
                <div key={business.id} data-testid="business-option">
                  <h6 className="fw-bold">{business.legalName}</h6>
                  {registrationDate && (
                    <div className="mb-2">
                      <FormattedMessageDiv className="fs-8" id="businessSelect.registrationDate" />
                      <div>{registrationDate}</div>
                    </div>
                  )}
                  <div>
                    <FormattedMessageDiv className="fs-8" id="businessSelect.businessAddress" />
                    {business.businessAddress?.address ? (
                      <>
                        <div>{business.businessAddress.address}</div>
                        {cityLine && <div>{cityLine}</div>}
                      </>
                    ) : (
                      <FormattedMessageDiv id="businessSelect.unknownAddress" />
                    )}
                  </div>
                </div>
              );
            })}
          </ToggleCardGroup>
          <Card
            bg={noneSelected ? 'light-gray' : 'white'}
            className={'shadow-none ' + (noneSelected ? 'border border-primary' : 'border')}
            bodyClassName="p-0"
            role="button"
            tabIndex={0}
            onClick={() => {
              setNoneSelected(true);
              setSelectedBusiness(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setNoneSelected(true);
                setSelectedBusiness(undefined);
              }
            }}
            data-testid="none-of-the-above"
          >
            <Stack direction="horizontal" gap={3} className="p-3 align-items-start fw-600 lh-base">
              <input type="radio" name="business-selection" aria-label="None of the above" readOnly checked={noneSelected} className="mt-2 flex-shrink-0" />
              <div>
                <FormattedMessageDiv className="h6 fw-bold" id="businessSelect.noneOfTheAbove" />
                <FormattedMessageDiv className="fw-normal" id="businessSelect.noneOfTheAbove.description" />
              </div>
            </Stack>
          </Card>
          <Stack gap={4} className="mt-2">
            <Button
              style={{ width: '100%' }}
              onClick={() => {
                if (noneSelected) onNoneSelected?.();
                else if (selectedBusiness) onSelect(selectedBusiness);
              }}
            >
              Continue
            </Button>
            <Button variant="outline-primary" style={{ width: '100%' }} onClick={() => onBack?.()}>
              Back
            </Button>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
};

export default BusinessSelect;
